import { useState } from 'react'
import { ArrowDown, ArrowUp, Pin, X } from 'lucide-react'
import { AdvancedDisclosure, Button, Field, IconButton, Input, Select, Textarea } from '@/components/common'
import { COLLECTION_REASONS, collectionChoices, collectionSportLabel, collectionStateText, newCollection } from './collectionModel'
import { ReferencePicker, AudiencesInput } from './ReferencePicker'
import { NativeEventSearch } from './NativeEventSearch'
import styles from './collectionEditor.module.css'

function Picker({ label, values, choices, disabled, max = 20, placeholder, onChange }) {
  const byID = new Map(choices.map((choice) => [choice.id, choice.name]))
  return <div className={styles.picker}><Field label={label}><Select size="compact" value="" disabled={disabled || values.length >= max} onChange={(event) => { if (event.target.value) onChange([...values, event.target.value]) }}><option value="">{values.length ? 'Add another' : (placeholder || `Any ${label.toLowerCase()}`)}</option>{choices.filter((choice) => !values.includes(choice.id)).map((choice) => <option key={choice.id} value={choice.id}>{choice.name}</option>)}</Select></Field>
    {values.length ? <ul className={styles.chips}>{values.map((value) => <li key={value}><span>{byID.get(value) || value}</span><IconButton label={`Remove ${byID.get(value) || value}`} disabled={disabled} onClick={() => onChange(values.filter((item) => item !== value))}><X size={12} /></IconButton></li>)}</ul> : null}
  </div>
}
const SELECTABLE_JURISDICTIONS = [
  { value: 'US-NJ', label: 'US-NJ (New Jersey)' },
  { value: 'US-PA', label: 'US-PA (Pennsylvania)' },
]

function LocalRulesEditor({ local, disabled, teamChoices, competitionChoices, onChange }) {
  const rules = local?.rules || []
  const defaultJurisdiction = local?.default_jurisdiction || ''

  const updateDefault = (val) => {
    const next = { ...(local || { rules: [] }), default_jurisdiction: val || undefined }
    if (!val) delete next.default_jurisdiction
    onChange(next)
  }

  const updateRule = (index, updatedRule) => {
    const nextRules = [...rules]
    nextRules[index] = updatedRule
    onChange({ ...(local || {}), rules: nextRules })
  }

  const removeRule = (index) => {
    const nextRules = rules.filter((_, i) => i !== index)
    // The contract requires at least one rule; removing the last rule removes Local.
    onChange(nextRules.length ? { ...(local || {}), rules: nextRules } : undefined)
  }

  const existingJurisdictions = new Set(rules.map((r) => r.jurisdiction))
  const availableJurisdictions = SELECTABLE_JURISDICTIONS.filter((j) => !existingJurisdictions.has(j.value))

  const addRule = () => {
    if (rules.length >= 8 || availableJurisdictions.length === 0) return
    const nextJur = availableJurisdictions[0].value
    const newRule = { jurisdiction: nextJur, teams: [], competitions: [] }
    onChange({ ...(local || {}), rules: [...rules, newRule] })
  }

  return (
    <fieldset className={styles.nestedFieldset}>
      <legend>Local rules</legend>
      <Field label="Default regulation">
        <Select
          size="compact"
          value={defaultJurisdiction}
          disabled={disabled}
          onChange={(e) => updateDefault(e.target.value)}
        >
          <option value="">None (anonymous empty)</option>
          {SELECTABLE_JURISDICTIONS.map((j) => (
            <option key={j.value} value={j.value}>{j.label}</option>
          ))}
        </Select>
      </Field>
      {rules.map((rule, index) => {
        const usedByOthers = new Set(rules.filter((_, i) => i !== index).map((r) => r.jurisdiction))
        const options = SELECTABLE_JURISDICTIONS.filter((j) => !usedByOthers.has(j.value))
        return (
          <div key={index} className={styles.ruleCard}>
            <div className={styles.ruleHeader}>
              <Field label={`Rule ${index + 1} regulation`}>
                <Select
                  size="compact"
                  value={rule.jurisdiction}
                  disabled={disabled}
                  onChange={(e) => updateRule(index, { ...rule, jurisdiction: e.target.value })}
                >
                  {options.map((j) => (
                    <option key={j.value} value={j.value}>{j.value}</option>
                  ))}
                </Select>
              </Field>
              <IconButton
                label={`Remove rule ${index + 1}`}
                disabled={disabled}
                onClick={() => removeRule(index)}
              >
                <X size={14} />
              </IconButton>
            </div>
            <ReferencePicker
              label="Local teams"
              values={rule.teams || []}
              choices={teamChoices}
              disabled={disabled}
              onChange={(teams) => updateRule(index, { ...rule, teams })}
            />
            <ReferencePicker
              label="Local competitions"
              values={rule.competitions || []}
              choices={competitionChoices}
              disabled={disabled}
              onChange={(competitions) => updateRule(index, { ...rule, competitions })}
            />
          </div>
        )
      })}
      {rules.length < 8 && availableJurisdictions.length > 0 ? (
        <Button size="compact" variant="secondary" disabled={disabled} onClick={addRule}>
          Add local rule
        </Button>
      ) : null}
    </fieldset>
  )
}
function SearchQuery({ value, disabled, onChange }) {
  const [draft, setDraft] = useState(value || '')
  return <form onSubmit={(event) => { event.preventDefault(); onChange(draft.trim()) }} className={styles.query}>
    <Field label="Saved search"><Input size="compact" value={draft} maxLength={200} disabled={disabled} placeholder="Team, competition or matchup" onChange={(event) => setDraft(event.target.value)} onBlur={() => { if (draft.trim().length >= 2) onChange(draft.trim()) }} /></Field>
    <Button type="submit" size="compact" disabled={disabled || draft.trim().length < 2}>Apply</Button>
  </form>
}
function AdvancedIDs({ definition, disabled, onChange }) {
  return <AdvancedDisclosure label="Advanced IDs"><div className={styles.controls}>{[['team_ids', 'Team IDs'], ['event_ids', 'Event IDs']].map(([field, label]) => <Field key={field} label={label}><Textarea size="compact" disabled={disabled} value={definition[field].join(', ')} onChange={(event) => onChange(field, [...new Set(event.target.value.split(',').map((value) => value.trim()).filter(Boolean))].slice(0, 20))} /></Field>)}</div></AdvancedDisclosure>
}
export function CollectionEditor({ component, catalog, slug, disabled, onChange }) {
  const definition = component.collection
  const hubContext = catalog.hub_context
  const result = definition ? catalog.collection_results?.[definition.id] : null
  const base = collectionChoices(catalog.events)
  const choices = result?.choices || {}
  const field = (key, value) => onChange({ collection: { ...definition, [key]: value } })
  const events = new Map([...base.events, ...(choices.events || [])].map((event) => [event.id, event.name]))
  const teamReferenceChoices = [...new Map([...base.teams, ...(choices.teams || [])].map((team) => [`${team.sport}:${team.id}`, { sport: team.sport, id: team.id, name: team.name }])).values()]
  const competitionReferenceChoices = [...new Map([...(base.competition_scopes || []), ...(choices.competition_scopes || [])].map((comp) => [`${comp.sport}:${comp.id}`, { sport: comp.sport, id: comp.id, name: comp.name }])).values()]
  const pin = (id) => field('pinned_event_ids', definition.pinned_event_ids.includes(id) ? definition.pinned_event_ids.filter((item) => item !== id) : [...definition.pinned_event_ids, id].slice(0, 20))
  const movePin = (index, offset) => { const next = [...definition.pinned_event_ids]; [next[index], next[index + offset]] = [next[index + offset], next[index]]; field('pinned_event_ids', next) }
  const stateText = collectionStateText(result)
  const exclusions = result?.decision?.explanations?.filter((item) => !item.codes.includes('scope_match')) || []
  return <fieldset className={styles.editor}><legend>Collection</legend>
    <Field label="Selection"><Select size="compact" value={definition ? 'smart' : 'manual'} disabled={disabled} onChange={(event) => onChange(event.target.value === 'smart' ? { collection: { ...newCollection(component), ...(hubContext ? { sports: [hubContext.sport], competitions: [hubContext.competition] } : {}) }, sport_ids: undefined, event_ids: undefined } : { collection: undefined, event_ids: undefined, sport_ids: [...new Set(catalog.events.map((event) => event.sport))] })}>{!hubContext || !definition ? <option value="manual">Manual sport selection</option> : null}<option value="smart">Smart collection</option></Select></Field>
    {hubContext ? <p>Games stay within {hubContext.competition}. Search and filters narrow this league.</p> : null}
    {definition ? <>
      <Field label="Source"><Select size="compact" value={definition.kind} disabled={disabled} onChange={(event) => onChange({ collection: { ...definition, kind: event.target.value, query: event.target.value === 'search' ? '' : null, order: event.target.value === 'search' ? 'relevance' : 'start_time' } })}><option value="inventory">Game inventory</option><option value="search">Saved sports search</option></Select></Field>
      {definition.kind === 'search' ? <SearchQuery key={`${definition.id}:${definition.kind}:${definition.query}`} value={definition.query} disabled={disabled} onChange={(value) => field('query', value)} /> : null}
      <Picker label="Sport" max={8} values={definition.sports} choices={[...new Set([...base.sports, ...(choices.sports || []), ...definition.sports])].map((id) => ({ id, name: collectionSportLabel(id) }))} disabled={disabled} onChange={(value) => field('sports', value)} />
      <Picker label="Competition" max={8} values={definition.competitions} choices={[...new Set([...base.competitions, ...(choices.competitions || [])])].map((id) => ({ id, name: id }))} disabled={disabled} onChange={(value) => field('competitions', value)} />
      <Picker label="Team" values={definition.team_ids} choices={[...new Map([...base.teams, ...(choices.teams || [])].map((team) => [team.id, team])).values()]} disabled={disabled} onChange={(value) => field('team_ids', value)} />
      <div className={styles.columns}>
        <Field label="Game state"><Select size="compact" value={definition.state} disabled={disabled} onChange={(event) => field('state', event.target.value)}><option value="active">Active games</option><option value="upcoming">Upcoming</option><option value="live">Live</option></Select></Field>
        <Field label="Order"><Select size="compact" value={definition.order} disabled={disabled} onChange={(event) => field('order', event.target.value)}><option value="start_time">Soonest start</option><option value="live_first">Live first</option><option value="preferences_first">Following first</option>{definition.kind === 'search' ? <option value="relevance">Search relevance</option> : null}</Select></Field>
      </div>
      <div className={styles.columns}>
        <Field label="Following">
          <Select
            size="compact"
            value={definition.following}
            disabled={disabled}
            onChange={(event) => {
              const val = event.target.value
              if (val === 'local') {
                const currentLocal = definition.local
                const local = currentLocal?.rules?.length ? currentLocal : { default_jurisdiction: 'US-NJ', rules: [{ jurisdiction: 'US-NJ', teams: [], competitions: [] }] }
                onChange({ collection: { ...definition, following: val, local } })
              } else {
                field('following', val)
              }
            }}
          >
            <option value="all">All matching games</option>
            <option value="teams">Followed teams only</option>
            <option value="competitions">Followed competitions only</option>
            <option value="any">Followed teams or competitions</option>
            <option value="local">Local to the customer</option>
          </Select>
        </Field>
        <Field label="Interests">
          <Select
            size="compact"
            value={definition.interests || 'declared'}
            disabled={disabled}
            onChange={(event) => field('interests', event.target.value === 'declared' ? undefined : event.target.value)}
          >
            <option value="declared">Declared following only</option>
            <option value="declared_and_history">Following plus wagering history</option>
          </Select>
        </Field>
      </div>
      {definition.following === 'local' || definition.local ? (
        <LocalRulesEditor
          local={definition.local}
          disabled={disabled}
          teamChoices={teamReferenceChoices}
          competitionChoices={competitionReferenceChoices}
          onChange={(local) => field('local', local)}
        />
      ) : null}
      <AudiencesInput
        audiences={definition.audiences}
        disabled={disabled}
        max={4}
        label="IMS audiences"
        format="ims"
        onChange={(audiences) => field('audiences', audiences)}
      />
      <Field label="Games to show"><Input size="compact" type="number" min={1} max={20} value={definition.limit} disabled={disabled} onChange={(event) => field('limit', Math.max(1, Math.min(20, Number(event.target.value) || 1)))} /></Field>
      <div className={styles.preview} aria-label="Collection results">
        <header><strong>{result?.matched !== undefined ? `${result.matched} matching game${result.matched === 1 ? '' : 's'}` : 'Result preview'}</strong><Button size="compact" variant="ghost" disabled={result?.refreshing || !result?.refresh} onClick={() => result.refresh()}>Refresh</Button></header>
        {stateText ? <p role="status">{stateText}</p> : null}
        <small>{result?.source === 'preview' ? 'Rehearsal' : result?.source === 'fixture' ? 'Sample inventory' : 'Live inventory'}{result?.complete ? ' · Complete scope' : ''}</small>
        {result?.events?.length ? <ol className={styles.results}>{result.events.map((event) => {
          const codes = result.decision?.explanations?.find((item) => item.event_id === event.id)?.codes || []
          return <li key={event.id}><div><strong>{event.name}</strong><span>{event.league}</span>{codes.length > 0 && <details><summary>Why is this shown?</summary>{codes.map((code) => <p key={code}>{COLLECTION_REASONS[code] || code}</p>)}</details>}</div><div className={styles.rowActions}><IconButton label={`${definition.pinned_event_ids.includes(event.id) ? 'Unpin' : 'Pin'} ${event.name}`} disabled={disabled} onClick={() => pin(event.id)}><Pin size={14} fill={definition.pinned_event_ids.includes(event.id) ? 'currentColor' : 'none'} /></IconButton><IconButton label={`Exclude ${event.name}`} disabled={disabled || definition.excluded_event_ids.length >= 20} onClick={() => field('excluded_event_ids', [...definition.excluded_event_ids, event.id])}><X size={14} /></IconButton></div></li>
        })}</ol> : null}
      </div>
      <NativeEventSearch label="Pin a game" placeholder="Search a game to pin" disabled={disabled || definition.pinned_event_ids.length >= 20} catalog={catalog} slug={slug} onSelect={(event) => { if (!definition.pinned_event_ids.includes(event.id)) pin(event.id) }} />
      {definition.pinned_event_ids.length ? <ol className={styles.pins}>{definition.pinned_event_ids.map((id, index) => <li key={id}><span>{events.get(id) || id}</span><div className={styles.rowActions}><IconButton label={`Move ${events.get(id) || id} earlier`} disabled={disabled || index === 0} onClick={() => movePin(index, -1)}><ArrowUp size={12} /></IconButton><IconButton label={`Move ${events.get(id) || id} later`} disabled={disabled || index === definition.pinned_event_ids.length - 1} onClick={() => movePin(index, 1)}><ArrowDown size={12} /></IconButton><IconButton label={`Unpin ${events.get(id) || id}`} disabled={disabled} onClick={() => pin(id)}><X size={12} /></IconButton></div></li>)}</ol> : null}
      <NativeEventSearch label="Exclude a game" placeholder="Search a game to exclude" disabled={disabled || definition.excluded_event_ids.length >= 20} catalog={catalog} slug={slug} onSelect={(event) => { if (!definition.excluded_event_ids.includes(event.id)) field('excluded_event_ids', [...definition.excluded_event_ids, event.id]) }} />
      {definition.excluded_event_ids.length ? <ul className={styles.chips}>{definition.excluded_event_ids.map((id) => <li key={id}><span>{events.get(id) || id}</span><IconButton label={`Remove ${events.get(id) || id}`} disabled={disabled} onClick={() => field('excluded_event_ids', definition.excluded_event_ids.filter((item) => item !== id))}><X size={12} /></IconButton></li>)}</ul> : null}
      {exclusions.length ? <details className={styles.exclusions}><summary>Exclusion reasons ({exclusions.length})</summary><ul>{exclusions.slice(0, 20).map((item) => <li key={item.event_id}><strong>{events.get(item.event_id) || item.event_id}</strong><span>{item.codes.map((code) => COLLECTION_REASONS[code] || code).join(' · ')}</span></li>)}</ul>{exclusions.length > 20 ? <p>Showing 20 of {exclusions.length} excluded games. Narrow the collection to inspect more.</p> : null}</details> : null}
      <Field label="When no games match"><Select size="compact" value={definition.empty_behavior} disabled={disabled} onChange={(event) => field('empty_behavior', event.target.value)}><option value="message">Show an empty message</option><option value="hide">Hide the section</option></Select></Field>
      <Field label="When inventory is unavailable"><Select size="compact" value={definition.unavailable_behavior} disabled={disabled} onChange={(event) => field('unavailable_behavior', event.target.value)}><option value="cached">Show recently loaded games</option><option value="message">Show an unavailable message</option></Select></Field>
      <AdvancedIDs definition={definition} disabled={disabled} onChange={field} />
    </> : null}
  </fieldset>
}
